import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { HandleSelect, isValidEntityType, MetadataType } from "types";
import { connect, useDispatch, useSelector } from "react-redux";
import { State } from "redux/store";
import classNames from "classnames";
import {
  getAllSelectedFilters,
  getClusterPageScrollPosition,
  getCurrentFilterQueries,
  getDataPageScrollPosition,
  getDatasetContextualLabels,
  getExportRequestData,
  getFetchError,
  getGlobalMDType,
  getIsCardslistLoading,
  getIsSearchByRelevanceEnabled,
  getIsShowDebugInfoEnabled,
  getLastSelectedIndex,
  getNavigationClusterLoader,
  getNoResultsFound,
  getRelevanceScores,
  getSearchOptions,
  getShowQueryPanel,
  getSimilarityVertexCluster,
  getTempExportCount,
  getTemporaryAssignedTags,
  getTemporaryExportDataArray,
  getUserTags,
} from "redux/SingleDataset/selectors";

import {
  addAllForTempExport,
  addAllMediaIDsFromClusterPageToTemp,
  clearFilters,
  clearSelectedMedia,
  fetchDataset,
  fetchDatasetContextualLabels,
  fetchTemporaryAssignedTags,
  fetchUserTags,
  setGlobalMDType,
  setLastSelectedIndex,
  setTemporaryAssignedTags,
  updateTemporaryExportData,
} from "redux/SingleDataset/actions";
import {
  calculatePlaceHolderCount,
  useWindowResize,
} from "helpers/utility/utilities";
import {
  ENTITY_TYPE_PARAMETER,
  GRANULARITY_PARAMETER,
  LABEL_CLASS_PARAMETER,
  LABELS_PARAMETER,
  PAGE_PARAMETER,
  REVIEW_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  SEED_PARAMETER,
  USER_TAGS_PARAMETER,
  VERTEX_CONTEXT,
  VERTEX_ID,
  VERTEX_SIMILARITY,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";
import FetchErrorCard from "views/components/FetchErrorCard";
import {
  NoMatchFoundCard,
  NoMatchFoundCardProps,
  NoMatchFoundPlaceholder,
} from "views/components/NoMatchFoundCard";

import FloatingActions from "views/components/FloatingActions";
import QueryPanel from "views/components/QueryPanel";
import EnrichmentCard from "./EnrichmentCard";
import VertexCard from "./VertexCard";

import styles from "./style.module.scss";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import useQueryParam from "hooks/useQueryParam";
import useVqlParams from "hooks/useVqlParams";
import { createAndUpdateVisualSimilarityFilter } from "helpers/utility/vqlHelpers";
import FloatingPanel from "views/components/FloatingPanel";
import { Option, SelectListProps } from "views/uikit/FormFields/SelectList";
import { VLButtonProps } from "views/uikit/Buttons/VLButton";
import { RightPanelMode } from "../../../../providers/DataPageRightPanelProvider";
import { FloatingDetails } from "../../../../components/LabelPropagation/ReviewFloatingDetails";
import ClusterDetailsFilter from "../DataPageFilters/ClusterDetailsFilter";
import Masonry from "react-masonry-css";
import isEmpty from "lodash/isEmpty";

interface LabelPropagationProps {
  onLabelClick: (value: string[]) => void;
  options?: Option[];
  displayNewFloatingPanel?: boolean;
  showLabelPropagation: boolean;
}

interface DataListProps {
  isContentLoading: boolean;
  navigationCluster: any;
  navigationClusterTags: any;
  page: any;
  showMetadataSummary: boolean;
  fetchError: Error | null;
  isVertexSelected: boolean;
  exportDataArray: any[];
  lastSelectedIndex: any;
  dataPageScrollPosition: string;
  clusterPageScrollPosition: string;
  noResultsFound: boolean;
  clusters: Cluster[];
  labelPropagation: LabelPropagationProps;
  isDataPage: boolean;
  rightPanelMode: RightPanelMode;
  title?: ReactNode;
  bulkActionList?: ActionItem[];
  isReview?: boolean;
  emptyStateProps?: NoMatchFoundCardProps;
  reviewCounters?: ReactNode;
}

export interface Label {
  name: string;
  type?: "VL" | "User";
}

export interface Cluster {
  id: string;
  label: Label;
  title: string;
  cluster_id: string;
  previews: Preview[];
  onDetailsClick?: (data: Cluster) => void;
  floatingDetails?: string;
  clusterLabel?: string;
  flat_response?: boolean;
  __id?: string;
}

export type PreviewBorderColor = "green" | "red";

export interface ActionItem {
  action: VLButtonProps;
  subAction?: SelectListProps;
  tooltip?: string; // tooltip text to display on hover
}

export interface Preview {
  image_id: string;
  media_id: string;
  type: "IMAGE" | "OBJECT";
  video_uri?: string;
  title?: string;
  actionList?: ActionItem[];
  modalActionList?: ActionItem[];
  borderColor?: PreviewBorderColor;
  opacity: number;
  media_uri: string | null;
  relevanceType: string | null;
  floatingDetails?: FloatingDetails;
  label: Label;
  displayIgnorLayout?: boolean;
  onDetailsClick: (data: Preview) => void;
  completed?: boolean;
  labelId: string;
  video_id?: string;
  frame_timestamp?: number;
}

const getInitialMdType = (items: any[], isObjects: boolean) => {
  let hasLabels, hasCaptions, hasUserTags;
  for (const item of items) {
    const previews = item.previews || [item];
    for (const p of previews) {
      if (!isEmpty(p.labels)) {
        hasLabels = true;
      }
      if (!isEmpty(p.caption)) {
        hasCaptions = true;
      }
      if (!isEmpty(p.user_tags)) {
        hasUserTags = true;
      }
    }
  }
  if (hasLabels) {
    return isObjects ? MetadataType.OBJECT_LABELS : MetadataType.IMAGE_LABELS;
  }
  if (hasCaptions) {
    return isObjects
      ? MetadataType.OBJECT_CAPTIONS
      : MetadataType.IMAGE_CAPTIONS;
  }
  if (hasUserTags) {
    return MetadataType.USER_TAGS;
  }
  return isObjects ? MetadataType.OBJECT_LABELS : MetadataType.IMAGE_LABELS;
};

const DataList = ({
  isContentLoading,
  navigationCluster,
  navigationClusterTags,
  page,
  showMetadataSummary,
  fetchError,
  isVertexSelected,
  exportDataArray,
  lastSelectedIndex,
  dataPageScrollPosition,
  clusterPageScrollPosition,
  noResultsFound,
  labelPropagation,
  clusters,
  isDataPage,
  rightPanelMode,
  title,
  bulkActionList,
  isReview,
  emptyStateProps,
  reviewCounters,
}: DataListProps) => {
  const [placeholderCount, setPlaceholderCount] = useState(0);
  const [indexOfPointerOut, setIndexOfPointerOut] = useState(-1);
  const globalMDType = useSelector(getGlobalMDType);

  const dispatch = useDispatch();
  const { vqlQueries, addVqlQuery } = useVqlParams();
  const queryParamsFilters = useAllQueryParamsFilters();

  const { datasetId, clusterId } = useParams();
  const { height, width } = useWindowResize();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const isFlatResponse = useMemo(
    () => clusters.some(({ flat_response }) => flat_response),
    [clusters],
  );
  const inReview = searchParams.has(REVIEW_PARAMETER);
  const labelId = searchParams.has(LABEL_CLASS_PARAMETER);

  const entityTypeFromQueryParam = isValidEntityType(
    queryParamsFilters[ENTITY_TYPE_PARAMETER],
  )
    ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
    : null;
  const labelFromQueryParam = useMemo(
    () => JSON.parse(queryParamsFilters[LABELS_PARAMETER] || "[]"),
    [queryParamsFilters],
  );
  const userTagsFromQueryParam = useMemo(
    () => JSON.parse(queryParamsFilters[USER_TAGS_PARAMETER] || "[]"),
    [queryParamsFilters],
  );
  const searchTextParam = queryParamsFilters[SEARCH_TEXT_PARAMETER] || null;

  const currentFilterQueries = useSelector((state) =>
    getCurrentFilterQueries(state, queryParamsFilters, false, false),
  );

  const allSelectedFilters = useSelector((state) =>
    getAllSelectedFilters(state, queryParamsFilters),
  );
  const showQueryPanel = useSelector((state: State) =>
    getShowQueryPanel(state, vqlQueries),
  );
  const exportRequestData = useSelector((state) =>
    getExportRequestData(state, queryParamsFilters, true),
  );
  const datasetLabels = useSelector((state) =>
    getDatasetContextualLabels(state, labelFromQueryParam),
  );
  const datasetUserTags = useSelector((state: State) =>
    getUserTags(state, userTagsFromQueryParam),
  );
  const rawRelevanceScores = useSelector((state: any) =>
    getRelevanceScores(state, entityTypeFromQueryParam),
  );
  const searchOptions = useSelector((state) =>
    getSearchOptions(state, entityTypeFromQueryParam),
  );
  const isSearchByRelevanceEnabled = useSelector((state) =>
    getIsSearchByRelevanceEnabled(state, entityTypeFromQueryParam),
  );
  const exportData = useSelector(getTemporaryExportDataArray);
  const showDebugInfo = useSelector(getIsShowDebugInfoEnabled);
  const isCardslistLoading = useSelector(getIsCardslistLoading);
  const isNavigationClusterLoading = useSelector(getNavigationClusterLoader);

  const assignedTags = useSelector(getTemporaryAssignedTags);
  const exportCount = useSelector(getTempExportCount);
  const showFloatingActions =
    exportCount > 0 &&
    !exportDataArray.some(({ trigger }) => trigger === "drag");

  const showFeedbackInCard = exportCount === 0;

  const cardContainer = useRef<HTMLDivElement>(null);

  const isClusterPage = !!datasetId && !!clusterId;
  const showSelectOption = isDataPage || isClusterPage || (inReview && labelId);

  const sortedList = useMemo(() => {
    if (fetchError) {
      return [];
    }
    if (isDataPage) {
      return clusters;
    } else {
      if (navigationCluster) {
        return [...navigationCluster.previews];
      }
      return [];
    }
  }, [fetchError, isDataPage, clusters, navigationCluster]);

  const clusterTagsByClusterId = useMemo(() => {
    if (fetchError) {
      return {};
    }
    if (isDataPage) {
      if (clusters?.length) {
        return clusters.reduce((acc: any, cluster: any) => {
          acc[cluster.cluster_id] = cluster;
          return acc;
        }, {});
      }
      return {};
    } else {
      if (navigationClusterTags?.previews) {
        return navigationClusterTags.previews.reduce(
          (acc: any, preview: any) => {
            acc[preview.media_id] = preview;
            return acc;
          },
          {},
        );
      }
      return {};
    }
  }, [fetchError, isDataPage, clusters, navigationClusterTags]);

  let length = isContentLoading
    ? 1
    : isVertexSelected
      ? sortedList.length + 1
      : sortedList.length;
  const showPlaceholders =
    !isVertexSelected && (isContentLoading || sortedList.length === 0);

  const pageNumber = useQueryParam(PAGE_PARAMETER);
  const lastPosition = isDataPage
    ? dataPageScrollPosition
    : clusterPageScrollPosition;

  const showRelevance =
    searchOptions.semanticSearch &&
    isSearchByRelevanceEnabled &&
    searchTextParam;
  const relevanceScores = useMemo(
    () => (showRelevance ? rawRelevanceScores : null),
    [showRelevance, rawRelevanceScores],
  );

  const [actualColumnsCount, setActualColumnsCount] = useState(0);
  useEffect(() => {
    const calculateActualColumnsCount = () => {
      if (cardContainer.current) {
        let count;
        const { gridTemplateColumns } = getComputedStyle(cardContainer.current);
        if (gridTemplateColumns.startsWith("repeat")) {
          count = Number(
            gridTemplateColumns.substring(7, gridTemplateColumns.indexOf(",")),
          );
        } else {
          count = gridTemplateColumns.split(" ").length;
        }
        setActualColumnsCount(count);
      }
    };
    calculateActualColumnsCount();
    window.addEventListener("resize", calculateActualColumnsCount);
    return () => {
      window.removeEventListener("resize", calculateActualColumnsCount);
    };
  }, []);

  const showMasonryLayout = false;

  const listClassNames = classNames(styles.list, {
    [styles.metadatasummaryList]: rightPanelMode === "summary",
    [styles.propagationList]: rightPanelMode === "labels",
    [styles.hiddenOverflowY]: length <= 0,
  });

  const removeParameters = (searchParams: URLSearchParams) => {
    let keysToBeDeleted: any = [];
    searchParams.forEach((value: any, key: any) => {
      //ignoring the filters that are not reset
      if (
        [
          PAGE_PARAMETER,
          GRANULARITY_PARAMETER,
          ENTITY_TYPE_PARAMETER,
          SEED_PARAMETER,
        ].includes(key)
      ) {
        //do nothing
      } else {
        keysToBeDeleted.push(key);
      }
    });

    if (keysToBeDeleted.length > 0) {
      keysToBeDeleted.forEach((key: any) => searchParams.delete(key));
    }
    return searchParams;
  };

  const onClearFilters = async () => {
    dispatch(clearFilters() as any);

    if (!isVertexSelected) {
      searchParams.set(PAGE_PARAMETER, "1");
    }
    const updatedSearchParams = removeParameters(searchParams);
    const newParams = `?${updatedSearchParams.toString()}`;
    let newPath = location.pathname;

    if (newPath.includes("/cluster")) {
      newPath = newPath.split("/cluster")[0];
    }
    dispatch(
      fetchDatasetContextualLabels(datasetId, queryParamsFilters) as any,
    );
    dispatch(fetchUserTags(datasetId, queryParamsFilters) as any);
    dispatch(fetchDataset(datasetId) as any);
    navigate(newPath.concat(newParams));
  };

  const showNoMatchFound = () => {
    if (fetchError) {
      return <FetchErrorCard />;
    } else if (!isContentLoading && length === 0 && !isVertexSelected) {
      return (
        <NoMatchFoundCard onActionClick={onClearFilters} {...emptyStateProps} />
      );
    }
  };

  const showNoMatchFoundPlaceholder =
    !isContentLoading && isVertexSelected && length === 1 && noResultsFound;

  const addVisualSimilarityVqlQuery = useCallback(
    (referenceImageId: string, negation?: boolean, path?: string) => {
      const visualSimilarityQuery = createAndUpdateVisualSimilarityFilter(
        referenceImageId,
        undefined, // boundingBox - no bounding box for regular similarity clicks
        undefined, // id - will be auto-generated
        "media", // operation - use "media" for regular similarity clicks
        !!negation, // negation flag for exclude logic
      );

      addVqlQuery(visualSimilarityQuery, path);
    },
    [addVqlQuery],
  );

  const handleSetVertex = useCallback(
    (id: string, type: "MEDIA" | "CLUSTER", negation?: boolean) => {
      searchParams.set(PAGE_PARAMETER, "1");
      if (type === "MEDIA") {
        // Set legacy parameters unless VQL upgrade is enabled
        searchParams.set(VERTEX_ID, id);
        searchParams.set(VERTEX_TYPE, "MEDIA");
      } else if (type === "CLUSTER") {
        const vertexCluster = clusters?.find(
          (cluster: Cluster) => cluster.cluster_id === id,
        );
        if (!!vertexCluster) {
          searchParams.set(VERTEX_CONTEXT, currentFilterQueries);
          searchParams.set(VERTEX_ID, vertexCluster.previews[0].media_id);
          searchParams.set(VERTEX_TYPE, "CLUSTER");
        }
      }
      searchParams.delete(VERTEX_SIMILARITY);
      const path = isDataPage
        ? `/dataset/${datasetId}/data`
        : `/dataset/${datasetId}/data/cluster/${clusterId}`;
      navigate({
        pathname: path,
        search: `?${searchParams.toString()}`,
      });
    },
    //eslint-disable-next-line
    [
      dispatch,
      datasetId,
      navigate,
      searchParams,
      pageNumber,
      clusters,
      currentFilterQueries,
      addVisualSimilarityVqlQuery,
    ],
  );

  const updateExportDataForSingleItem = (
    singleData: any,
    clusterID: string,
  ) => {
    const actualData = singleData.flat_response
      ? singleData.previews[0]
      : singleData;

    const selectedExportData = { ...actualData, cluster_id: clusterID };
    const updateType = actualData.cluster_id ? "cluster" : "media";

    dispatch(
      updateTemporaryExportData(
        { ...selectedExportData, cluster_filters: allSelectedFilters },
        updateType,
      ) as any,
    );
  };

  const getSelectedExportItemIDs = (
    exportDataArray: any[],
    clusterId: string,
    isDataPage: boolean,
  ): any[] => {
    // check if the whole cluster is selected for MediaIDs
    const clusterForMediaIDs = exportDataArray.find(
      (item: any) =>
        item.cluster_id === clusterId && item.exportType === "cluster",
    );
    const selectedCluster = clusterForMediaIDs ? navigationCluster : undefined;

    // If data page, return all cluster IDs
    if (isDataPage) {
      return exportDataArray.map((item: any) =>
        isFlatResponse ? item.media_id : item.cluster_id,
      );
    }

    // If a selectedCluster exists, get only the selected previews
    if (selectedCluster) {
      return selectedCluster.previews
        .filter(
          (preview: any) =>
            !clusterForMediaIDs.excludeMediaIDs ||
            !clusterForMediaIDs.excludeMediaIDs.includes(preview.media_id),
        )
        .map((preview: any) => preview.media_id);
    }

    // Otherwise, return media IDs matchimg the specific cluster ID
    return exportDataArray
      .filter(
        (item: any) =>
          item.exportType === "media" && item.cluster_id === clusterId,
      )
      .map((item: any) => item.media_id);
  };

  const getTempArray = (
    sortedList: any[],
    lastIndex: string,
    currentIndex: string,
  ): any[] => {
    const lastIdx = lastIndex ? Number(lastIndex.split("index-")[1]) : 0;
    const currIdx = Number(currentIndex.split("index-")[1]);

    const isForward = currIdx > lastIdx;
    return isForward
      ? sortedList.slice(lastIdx, currIdx + 1)
      : sortedList.slice(currIdx, lastIdx + 1);
  };

  const handleSelect: HandleSelect = (
    e: MouseEvent,
    selected: boolean,
    specificIndex: string,
    clusterID: string,
    singleData: any,
  ) => {
    if (!e.shiftKey) {
      updateExportDataForSingleItem(singleData, clusterID);
      dispatch(setLastSelectedIndex(specificIndex));
      return;
    }

    const selectedExportItemIDs = new Set(
      getSelectedExportItemIDs(exportDataArray, clusterID, isDataPage),
    );

    // Fetch the temporary array
    const tempArray = getTempArray(
      sortedList,
      lastSelectedIndex,
      specificIndex,
    );

    tempArray.forEach((item: any) => {
      const actualItem = item.flat_response ? item.previews[0] : item;
      const dynamicID = actualItem.cluster_id || actualItem.media_id;
      const isItemSelected = selectedExportItemIDs.has(dynamicID);
      const dynamicClusterID = isDataPage
        ? item.cluster_id
        : navigationCluster.cluster_id;

      if ((selected && isItemSelected) || (!selected && !isItemSelected)) {
        updateExportDataForSingleItem(item, dynamicClusterID);
      }
    });

    dispatch(setLastSelectedIndex(specificIndex));
  };

  useEffect(() => {
    //clear  whenever page switches
    dispatch(setLastSelectedIndex(null));

    //eslint-disable-next-line
  }, [clusterId, datasetId]);

  useEffect(() => {
    cardContainer.current && cardContainer.current.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const extraWidth = 26;
    setPlaceholderCount(
      calculatePlaceHolderCount(
        cardContainer?.current,
        null,
        length,
        extraWidth,
      ),
    );
  }, [height, width, length, isVertexSelected, isContentLoading]);

  useEffect(() => {
    const container = cardContainer.current;
    if (!container) {
      return;
    }
    if (lastPosition) {
      const element = document.getElementById(lastPosition);
      if (element) {
        container.scroll({ top: element.offsetTop - 14 });
      } else {
        container.scrollTo(0, 0);
      }
    } else {
      container.scrollTo(0, 0);
    }
  }, [isContentLoading, lastPosition]);

  useEffect(() => {
    const selectedClusterOnDataPage =
      isClusterPage &&
      exportDataArray.find(
        (singleData: any) =>
          singleData.cluster_id === clusterId &&
          singleData.exportType === "cluster",
      );

    const selectAllMedia = (datasetId: string) => {
      dispatch(clearSelectedMedia() as any);
      dispatch(
        addAllMediaIDsFromClusterPageToTemp(
          datasetId,
          allSelectedFilters,
        ) as any,
      );
    };

    const handleSelectAll = () => {
      if (isClusterPage) {
        if (!!selectedClusterOnDataPage) {
          //will remove the selected cluster from cart and add medias
          dispatch(
            updateTemporaryExportData(
              {
                ...selectedClusterOnDataPage,
                cluster_filters: allSelectedFilters,
              },
              "cluster",
            ) as any,
          );
        }
        selectAllMedia(datasetId);
      } else if (!!datasetId && inReview && labelId) {
        selectAllMedia(datasetId);
      } else if (!!datasetId && !inReview) {
        dispatch(addAllForTempExport(clusters, allSelectedFilters) as any);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;
      const isTyping =
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          (active as HTMLElement).isContentEditable);

      if (isTyping) return;

      if ((e.ctrlKey || e.metaKey) && e.key === "a") {
        e.preventDefault();
        handleSelectAll();
      }
    };

    if (!isContentLoading && sortedList.length > 0) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line
  }, [isContentLoading, sortedList, inReview]);

  useEffect(() => {
    if (exportDataArray.length > 0) {
      dispatch(
        fetchTemporaryAssignedTags(datasetId!, exportRequestData) as any,
      );
    } else if (assignedTags.length > 0) {
      dispatch(setTemporaryAssignedTags([]));
    }
    //eslint-disable-next-line
  }, [exportDataArray]);

  const handleGlobalMDTypeChange = (type: MetadataType) => {
    if (type === globalMDType) return;
    dispatch(setGlobalMDType(type));
  };
  const renderFloatingPanel = () => {
    if (inReview && labelId && bulkActionList && bulkActionList.length > 0) {
      return <FloatingPanel type="buttons" actionList={bulkActionList} />;
    } else if (
      labelPropagation?.options &&
      labelPropagation.options.length > 0
    ) {
      return (
        <FloatingPanel
          type="selectList"
          onOptionSelect={(value: string[]) => {
            labelPropagation.onLabelClick(value);
          }}
          options={labelPropagation?.options}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    if (!inReview) {
      const isObjects = entityTypeFromQueryParam === "OBJECTS";
      dispatch(setGlobalMDType(getInitialMdType(sortedList, isObjects)));
    }
  }, [inReview, sortedList, dispatch, entityTypeFromQueryParam]);

  const showClusterFilter = false;

  const renderVertex = () => {
    if (!isVertexSelected) {
      return null;
    }
    return (
      <VertexCard
        index={-2}
        globalMDType={globalMDType}
        cardContainerRef={cardContainer}
        indexOfPointerOut={indexOfPointerOut}
        showMetadataSummary={showMetadataSummary}
        //
        clusters={clusters}
        exportData={exportData}
        showDebugInfo={showDebugInfo}
        datasetLabels={datasetLabels}
        datasetUserTags={datasetUserTags}
        relevanceScores={relevanceScores}
        isCardslistLoading={isCardslistLoading}
        isNavigationClusterLoading={isNavigationClusterLoading}
        //

        onSelect={handleSelect}
        onIndexOfPointerOut={setIndexOfPointerOut}
        onGlobalMDTypeChange={handleGlobalMDTypeChange}
      />
    );
  };

  const renderCards = () =>
    sortedList?.map((singleData: any, index: number) => {
      const clusterID = isDataPage
        ? singleData.cluster_id
        : navigationCluster?.cluster_id;

      let userTagsData = isDataPage
        ? clusterTagsByClusterId[singleData.cluster_id]?.user_tags || {}
        : clusterTagsByClusterId[singleData.media_id]?.user_tags || [];

      return (
        <EnrichmentCard
          columnsCount={actualColumnsCount}
          page={page}
          key={index}
          index={index}
          isVertex={false}
          datasetId={datasetId!}
          singleData={singleData}
          clusterID={clusterID}
          isDataPage={isDataPage}
          globalMDType={globalMDType}
          containerRef={cardContainer}
          isClusterPage={isClusterPage}
          userTagsData={userTagsData || []}
          showSelectOption={showSelectOption}
          indexOfPointerOut={indexOfPointerOut}
          showMetadataSummary={showMetadataSummary}
          //
          clusters={clusters}
          exportData={exportData}
          showDebugInfo={showDebugInfo}
          datasetLabels={datasetLabels}
          datasetUserTags={datasetUserTags}
          relevanceScores={relevanceScores}
          isCardslistLoading={isCardslistLoading}
          isNavigationClusterLoading={isNavigationClusterLoading}
          navigationCluster={navigationCluster}
          //
          onSelect={handleSelect}
          onSetVertex={handleSetVertex}
          onIndexOfPointerOut={setIndexOfPointerOut}
          onGlobalMDTypeChange={handleGlobalMDTypeChange}
          cardTitle={singleData.title}
          showFeedbackInCard={showFeedbackInCard}
        />
      );
    });

  const masonryBreakpoints = useMemo(() => {
    const getCount = (value: number) => {
      let result = value;
      if (rightPanelMode) {
        result = value - 1;
      }
      return Math.max(result, 2);
    };
    return {
      default: getCount(5),
      3100: getCount(9),
      2550: getCount(8),
      2200: getCount(7),
      1920: getCount(6),
      1919: getCount(5),
      1720: getCount(4),
      1500: getCount(3),
    };
  }, [rightPanelMode]);

  return (
    <div className={styles.singleListWrapper} data-test-id="singleListWrapper">
      {(showClusterFilter || showQueryPanel) && !inReview && (
        <div className={styles.filtersPanel}>
          {showClusterFilter && (
            <ClusterDetailsFilter className={styles.clusterFilter} />
          )}
          {showQueryPanel && <QueryPanel datasetId={datasetId!} />}
        </div>
      )}
      {showFloatingActions &&
        (labelPropagation.displayNewFloatingPanel ? (
          renderFloatingPanel()
        ) : (
          <FloatingActions />
        ))}
      {title}
      <div
        className={listClassNames}
        id="data-page-cards-list"
        ref={cardContainer}
      >
        {showMasonryLayout ? (
          <Masonry
            breakpointCols={masonryBreakpoints}
            className={styles.masonryGrid}
            columnClassName={styles.masonryGridColumn}
          >
            {renderVertex()}
            {renderCards()}
          </Masonry>
        ) : (
          <>
            {renderVertex()}
            {renderCards()}
          </>
        )}

        {showPlaceholders &&
          [...Array(placeholderCount)].map((_, index) => (
            <div key={index} className={styles.cardPlaceHolder}>
              {showNoMatchFoundPlaceholder && index === 0 && (
                <NoMatchFoundPlaceholder onActionClick={onClearFilters} />
              )}
            </div>
          ))}
        {showNoMatchFound()}
      </div>
    </div>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    noResultsFound: getNoResultsFound(state),
    fetchError: getFetchError(state),
    isVertexSelected: !!getSimilarityVertexCluster(state),
    exportDataArray: getTemporaryExportDataArray(state),
    lastSelectedIndex: getLastSelectedIndex(state),
    dataPageScrollPosition: getDataPageScrollPosition(state),
    clusterPageScrollPosition: getClusterPageScrollPosition(state),
  };
};

export default connect(mapStatesToProps)(DataList);
